import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { htmlToText, truncate } from './seo.utils';

export const SITE_NAME = 'Truck Drivers USA';

const DEFAULT_IMAGE = '/images/og/tdusa-default.jpg';
const DEFAULT_IMAGE_ALT = 'Truck Drivers USA — One Application. Endless Opportunities.';
const DESCRIPTION_MAX = 160;

export interface PageSeo {
  /** Page-specific title, without the site name (it's appended to <title> automatically) */
  title: string;
  /** Plain text or HTML; it's converted to text and trimmed to ~160 characters */
  description: string;
  /** Root-relative or absolute image URL; falls back to the TDUSA share image */
  image?: string | null;
  imageAlt?: string;
  /** og:type — defaults to "website" */
  type?: 'website' | 'article';
  /** Canonical path; defaults to the current path without query string or fragment */
  path?: string;
}

/**
 * Sets the full set of SEO / social tags for a page in one call: <title>, description,
 * canonical, Open Graph and Twitter. Every tag is set on every page, so nothing is left
 * missing or carried over from the previously viewed page during client-side navigation.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  setPage(page: PageSeo): void {
    const description = truncate(htmlToText(page.description), DESCRIPTION_MAX);
    const url = this.absoluteUrl(page.path ?? this.router.url.split(/[?#]/)[0]);
    const usingDefaultImage = !page.image;
    const image = this.absoluteUrl(page.image || DEFAULT_IMAGE);
    const imageAlt = usingDefaultImage ? DEFAULT_IMAGE_ALT : (page.imageAlt ?? page.title);

    this.title.setTitle(`${page.title} | ${SITE_NAME}`);
    this.setCanonical(url);

    const tags: MetaDefinition[] = [
      { name: 'description', content: description },

      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:type', content: page.type ?? 'website' },
      { property: 'og:url', content: url },
      { property: 'og:title', content: page.title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:image:alt', content: imageAlt },
      { property: 'og:locale', content: 'en_US' },

      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: page.title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
      { name: 'twitter:image:alt', content: imageAlt },
    ];

    for (const tag of tags) {
      this.meta.updateTag(tag);
    }

    // The default share image has known dimensions; custom images don't
    if (usingDefaultImage) {
      this.meta.updateTag({ property: 'og:image:width', content: '1200' });
      this.meta.updateTag({ property: 'og:image:height', content: '630' });
    } else {
      this.meta.removeTag('property="og:image:width"');
      this.meta.removeTag('property="og:image:height"');
    }
  }

  /**
   * Public origin of the site. Uses the current host, but always https outside local
   * development — behind the load balancer the server sees plain http requests.
   */
  siteOrigin(): string {
    const location = this.document.location;
    const host = location?.host ?? '';
    const isLocal = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.test(host);
    return host ? `${isLocal ? location.protocol : 'https:'}//${host}` : '';
  }

  absoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    return `${this.siteOrigin()}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
  }

  private setCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
